import { memo, useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import _isEmpty from 'lodash/isEmpty';
//Local files
import { CardItem } from './FifthFlows.styled';
import FifthAnalysesList from 'components/Analyses/FifthAnalyses/List/List';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useApp from 'hooks/useApp';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuccess from 'hooks/useSuccess';
import useFifthFlows from 'hooks/useFifthFlows';
import useFifthAnalyses from 'hooks/useFifthAnalyses';
import { DELETE_FIFTH_FLOW } from 'helpers/confirmations';
import { checkEmptyString, checkValidEmail, needToUpdateField } from 'helpers';
import { fifthFlowsSelectors } from "features/fifthFlows";

const FifthFlows = () => {
  const { openDeleteDialog } = useApp();
  const { setSuccess } = useSuccess();
  const { getFifthFlow, createFifthFlow, updateFifthFlow } = useFifthFlows();
  const { getFifthAnalyses, clearLocalFifthAnalyses } = useFifthAnalyses();
  const id = useCustomSelector(fifthFlowsSelectors.getFifthFlowId);
  const fifthFlow = useCustomSelector(fifthFlowsSelectors.getFifthFlowData);
  const fetching = useCustomSelector(fifthFlowsSelectors.fifthFlowIsFetching);
  const [issueEmail, setIssueEmail] = useState({ value: '', error: '' });
  const [prompt, setPrompt] = useState({ value: '', error: '' });
  const [edit, setEdit] = useState(false);
  const [copied, setCopied] = useState(false);
  const [firstTimeOpened, setFirstTimeOpened] = useState(true);

  const handleEntering = (fifth_flow) => {
    setIssueEmail({ value: fifth_flow?.issue_email, error: '' });
    setPrompt({ value: fifth_flow?.prompt, error: '' });
  };
  const handleExited = () => {
    setIssueEmail({ value: '', error: '' });
    setPrompt({ value: '', error: '' });
    setEdit(false);
  };
  const handleSave = () => {
    let hasError = false;

    if (!checkValidEmail(issueEmail.value)) {
      setIssueEmail(prev => ({ ...prev, error: `Email is invalid` }) );
      hasError = true;
    }
    if (!checkEmptyString(prompt.value)) {
      setPrompt(prev => ({ ...prev, error: `Prompt can't be empty` }) );
      hasError = true;
    }
    if (hasError) return;

    const fifth_flow = {
      ...(needToUpdateField(fifthFlow?.issue_email, issueEmail.value) && {  issue_email: issueEmail.value }),
      ...(needToUpdateField(fifthFlow?.prompt, prompt.value) && { prompt: prompt.value }),
    };

    if (!_isEmpty(fifth_flow)) {
      if (!!id) {
        updateFifthFlow(fifth_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully updated');
          }
        })
        .finally(() => setEdit(false))
      } else {
        createFifthFlow(fifth_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully created');
          }
        })
      }
    } 
  }
  const handleDelete = () => openDeleteDialog({ id: '', type: DELETE_FIFTH_FLOW });
  const handleCopy = () => {
    if (!!fifthFlow?.email && !copied)
    {
      navigator.clipboard.writeText(fifthFlow.email);
      setCopied(true);
    }
  };
  const fetchFifthAnalyses = useCallback(() => {
    getFifthAnalyses({ offset: 0 })
  }, [getFifthAnalyses]);
  
  useEffect(() => {
    if (!id) {
      handleExited();
      clearLocalFifthAnalyses();
    };
  }, [id, clearLocalFifthAnalyses]);
  useEffect(() => {
    if (firstTimeOpened) {
      getFifthFlow()
      .then(({ payload, error }) => {
        if (!error) {
          const { fifth_flow } = payload;
          handleEntering(fifth_flow);
          fetchFifthAnalyses();
        }
      })
      .finally(() => setFirstTimeOpened(false));
    }
  }, [getFifthFlow, fetchFifthAnalyses, firstTimeOpened]);

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 5 }}>
      <Container maxWidth={'sm'}>
        <Card sx={{ p: 2 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Configuration</Typography>
            <Box>
              {!id && <Button variant='outlined' disabled={fetching} onClick={handleSave}>Create</Button>}
              {id && !edit && <Button variant='outlined' disabled={fetching} onClick={() => setEdit(true)}>Edit</Button>}
              {id && edit && <Button variant='outlined' disabled={fetching} sx={{ mr: '5px' }} onClick={handleSave}>Update</Button>}
              {id && edit && <Button variant='outlined' sx={{ color: '#E30000', borderColor: '#E30000' }} disabled={fetching} onClick={handleDelete}>Delete</Button>}
            </Box>
          </CardItem>
          <CardItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <Tooltip 
              title={!!fifthFlow?.email ? (copied ? 'Copied!' : 'Click to copy') : ''}
              followCursor
              onClose={() => setTimeout(() => setCopied(false), 300)} 
            >
              <Typography 
                variant='subtitle2'
                component='div' 
                sx={{ wordBreak: 'break-all', cursor: 'pointer' }}
                onClick={handleCopy}
              >
                {`Receiving Email: ${!!fifthFlow?.email ? fifthFlow.email : '-'}`}
              </Typography>
            </Tooltip>
            <TextField
              fullWidth
              margin='dense'
              label='Email'
              disabled={fetching || (!!id && !edit)}
              value={issueEmail.value}
              onChange={({ target: { value } }) => setIssueEmail({ value, error: '' })}
              helperText={issueEmail.error}
              error={!!issueEmail.error}
            />
            <TextField
              fullWidth
              label='Prompt'
              multiline
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={prompt.error}
              error={!!prompt.error}
              value={prompt.value}
              onChange={({ target: { value } }) => setPrompt({ value, error: '' })}
            />
          </CardItem>
        </Card>
        <Card sx={{ p: 2, mt: 3 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>History</Typography>
          <FifthAnalysesList />
        </Card>    
      </Container>
    </Box>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <FifthFlows {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);